/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * TierZero+ membership expiry countdown.
 *
 * Lived as an inline <script> inside sections/main-membership-page.liquid, against the
 * repository's own rule that JavaScript goes here. Its wording now comes from locales via data
 * attributes on the element, so it translates like everything else rather than being English
 * baked into a script tag.
 */
document.addEventListener('DOMContentLoaded', function () {
  var el = document.querySelector('[data-countdown-target]');
  if (!el) return;

  var target = new Date(el.getAttribute('data-countdown-target'));
  if (isNaN(target.getTime())) return;

  function label(n, one, many) {
    return (n === 1 ? one : many).replace('{{ count }}', n).replace('{{count}}', n);
  }

  function update() {
    var diff = target - new Date();

    if (diff <= 0) {
      el.textContent = el.getAttribute('data-countdown-expired') || '';
      return;
    }

    var days = Math.floor(diff / 86400000);
    if (days > 0) {
      el.textContent = label(days, el.getAttribute('data-countdown-day'), el.getAttribute('data-countdown-days'));
    } else {
      var hours = Math.floor((diff % 86400000) / 3600000);
      el.textContent = label(hours, el.getAttribute('data-countdown-hour'), el.getAttribute('data-countdown-hours'));
    }
  }

  update();
  setInterval(update, 60000);
});

/**
 * Pre-order consent.
 *
 * The checkbox stores a fingerprint of the pre-order lines as a cart attribute, not a boolean.
 * A boolean would still say yes after the customer adds a second pre-order they never saw the
 * terms for. The fingerprint moves with the cart, so consent goes stale the moment the cart does.
 *
 * Liquid renders the current fingerprint and whether the stored one matches. This only writes
 * the attribute and keeps the checkout button in step. Everything it enforces is cosmetic: a
 * cart permalink never renders the cart template, so the real gate is the validation function.
 */
document.addEventListener('DOMContentLoaded', function () {
  function apply(root) {
    var consent = (root || document).querySelector('[data-preorder-consent]');
    var buttons = (root || document).querySelectorAll('[data-checkout-button]');
    if (!buttons.length) return;

    // No pre-order in the cart means nothing to consent to, so leave the button alone.
    if (!consent) {
      buttons.forEach(function (b) { b.disabled = false; b.removeAttribute('aria-describedby'); });
      return;
    }

    var accepted = consent.getAttribute('data-accepted') === '1';
    if (!accepted) clearStaleStamps(consent);
    var reason = consent.querySelector('[data-preorder-blocked-reason]');
    buttons.forEach(function (b) {
      b.disabled = !accepted;
      b.classList.toggle('button--disabled', !accepted);
      // Say why it is disabled. Without this a screen reader gets a dead button and no reason.
      if (!accepted && reason && reason.id) {
        b.setAttribute('aria-describedby', reason.id);
      } else {
        b.removeAttribute('aria-describedby');
      }
    });
  }

  function write(fingerprint) {
    // fetch resolves on 4xx and 5xx, so without this check a rejected update would run the
    // success path and the UI would show consent as saved when nothing was stored.
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { preorder_policy_accepted: fingerprint } })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/update.js returned ' + response.status);
      return response;
    });
  }

  /**
   * The same consent, written again as a line item property.
   *
   * The cart attribute above is what the theme reads. This is what the checkout validation app
   * reads, and it is the better half: a property lives on the line, so a second pre-order added
   * later arrives without one and the gate closes again on its own.
   *
   * Three things make this more than one fetch call.
   *
   * /cart/change.js takes one line at a time, and it REPLACES that line's properties rather than
   * merging, so anything another app put there has to be copied forward by hand. Changing a
   * property also changes the line key, which can renumber or merge lines, so the cart is re-read
   * between every write rather than trusting indexes captured before the first one.
   *
   * Removing consent is NOT done by leaving the key out. Measured on the live cart 2026-09-08:
   * posting `properties: {}` is ignored outright and the line keeps everything it had. Sending the
   * key with an empty string does clear the value, but the key itself stays behind. So the code
   * always sends the key, with either the value or '', and decides whether a line is already in
   * the right state by comparing the VALUE rather than testing for the key's presence. Testing
   * presence is what made the first version loop until it ran out of budget.
   */
  function syncLineProperties(consent, accept) {
    if (!consent || consent.getAttribute('data-line-properties') !== '1') return Promise.resolve();

    var variants = (consent.getAttribute('data-preorder-variants') || '').split(',').filter(Boolean);
    var name = consent.getAttribute('data-property-name');
    var value = consent.getAttribute('data-property-value');
    if (!variants.length || !name) return Promise.resolve();

    // One write per pre-order line, plus headroom for lines merging underneath us. Without a
    // ceiling a line that refuses to take the property would recurse until the tab died.
    var budget = variants.length * 3 + 5;

    function step() {
      if (budget-- <= 0) throw new Error('line property sync did not settle');

      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('cart.js returned ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            if (variants.indexOf(String(item.variant_id)) === -1) continue;

            var props = item.properties || {};
            var target = accept ? value : '';
            var stampKey = '_' + 'preorder_terms_at';
            var stamped = !!props[stampKey];

            // Compare the value, not whether the key exists. Clearing leaves the key behind with
            // an empty value, so a presence test would report the line as still consented and
            // this walk would never finish.
            //
            // The timestamp is part of the test, not decoration. A cart permalink can arrive with
            // the consent property already set on its first line, and on value alone that line
            // would be skipped and reach the order with consent recorded but no record of when.
            // The timestamp is the evidence half of this feature, so a line missing it is not yet
            // in the state we want.
            var settled = accept ? (props[name] === target && stamped)
                                 : (props[name] === target && !stamped);
            if (settled) continue;

            var next = {};
            Object.keys(props).forEach(function (key) { next[key] = props[key]; });
            next[name] = target;
            // Underscore-prefixed, so it stays off the cart line but still reaches the order.
            next['_' + 'preorder_terms_at'] = accept ? new Date().toISOString() : '';

            // Quantity and selling plan have to be sent back or they are lost. Omitting quantity
            // resets the line to 1, measured on a live cart 2026-09-08: a six unit pre-order line
            // silently became one unit the moment consent was given. The customer would have paid
            // for one and never seen it happen.
            var body = { line: i + 1, properties: next, quantity: item.quantity };
            var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
            if (plan && plan.id) body.selling_plan = plan.id;

            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            }).then(function (response) {
              if (!response.ok) throw new Error('cart/change.js returned ' + response.status);
              return step();
            });
          }
          return null;
        });
    }

    return step();
  }

  /*
   * Strip the consent property from lines the theme no longer considers consented.
   *
   * Measured on live 2026-09-14. Consent at quantity 1, then raise the line to 2 from the cart
   * page: /cart/change.js keeps the line properties, so the line still reads "Accepted" while the
   * fingerprint has moved to x2. The theme correctly disables its own checkout button, but
   * /checkout is a URL anyone can type, and the validation app only ever sees the line. Typing it
   * reached a working checkout with consent that was given for a different cart.
   *
   * The theme cannot enforce anything at checkout, but it can refuse to leave behind a claim it
   * no longer stands behind. Clearing consent nobody re-confirmed fails safe: worst case the
   * customer ticks the box again.
   *
   * This does NOT make the gate tamper proof. A cart permalink can write any line property it
   * likes, including this one and its timestamp, which is measured and written up in
   * ops/docs/core/gate-testing-bible.md. Consent is a record, not a lock.
   */
  function clearStaleStamps(consent) {
    if (consent.getAttribute('data-stale-stamps') !== '1') return;
    // Cleared first, so a second apply() in the same tick cannot start the walk twice.
    consent.setAttribute('data-stale-stamps', '0');
    syncLineProperties(consent, false).catch(function (err) {
      console.error('Pre-order consent: could not clear stale line properties', err);
    });
  }

  document.addEventListener('change', function (event) {
    var input = event.target.closest && event.target.closest('.preorder-consent__input');
    if (!input) return;

    var consent = input.closest('[data-preorder-consent]');
    var fingerprint = input.checked ? consent.getAttribute('data-fingerprint') : '';

    input.disabled = true;
    // Line properties first, then the cart attribute. If the second half fails the button stays
    // disabled and the customer is told on the cart page. Done the other way round, a failure
    // would enable the button here and let the app refuse them at checkout instead, which is the
    // same outcome delivered at the worst possible moment.
    //
    // A failure between the two halves does leave the cart inconsistent: the lines say accepted
    // and the attribute does not. That is accepted rather than overlooked. It fails toward the
    // stricter of the two, because the theme reads the attribute and keeps the button disabled,
    // and it heals on the next tick, because the walk finds those lines already settled and goes
    // straight to writing the attribute. A rollback would mean a second write that can fail on
    // its own, in the middle of handling a failure.
    syncLineProperties(consent, input.checked)
      .then(function () { return write(fingerprint); })
      .then(function () {
        consent.setAttribute('data-accepted', input.checked ? '1' : '0');
        apply(document);
        // Re-render so the other copy of the cart, drawer or page, agrees with this one.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true, detail: { scrollToTop: false }
        }));
      })
      .catch(function (err) {
        console.error('Pre-order consent: could not save', err);
        input.checked = !input.checked;
      })
      .finally(function () { input.disabled = false; });
  });

  apply(document);
  document.addEventListener('cart:refresh', function () { setTimeout(function () { apply(document); }, 400); });
  document.addEventListener('cart:updated', function () { setTimeout(function () { apply(document); }, 400); });
  document.addEventListener('product:added', function () { setTimeout(function () { apply(document); }, 400); });
});

/*
 * Stop <buy-button> from treating every click inside it as "add to cart".
 *
 * The vendor element binds its handler to the CONTAINER, not to the button:
 *
 *   connectedCallback() { this.addEventListener("click", this.#onClickListener); }
 *
 * So it never checks where the click came from. Anything an app injects inside <buy-button>
 * becomes an add-to-cart trigger, and type="button" does not save it, because this is a click
 * listener on an ancestor rather than a form submit.
 *
 * Measured on live 2026-09-14: the wishlist app injects #amp-wishlist-save inside <buy-button> on
 * every product page. Clicking "Add to wishlist" put a GBP 669.95 pre-order in the basket. The
 * customer asked for a wishlist and got a purchase.
 *
 * The listener goes on the injected element itself, in the bubble phase, and calls
 * stopPropagation rather than stopImmediatePropagation. That distinction is the whole fix:
 * stopPropagation leaves other listeners on the SAME element running, so the app's own wishlist
 * handler still fires, and only the ancestor is kept from seeing the click.
 *
 * This guards the shape of the bug rather than this one app. Any future app that injects into
 * the buy button is covered without another patch.
 *
 * assets/theme.js is a vendor bundle and is not ours to edit, which is why this lives here.
 */
document.addEventListener('DOMContentLoaded', function () {
  var GUARD = 'buyButtonGuarded';

  function guard(root) {
    (root || document).querySelectorAll('buy-button').forEach(function (host) {
      host.querySelectorAll('button, a, input, select, textarea, [role="button"]').forEach(function (el) {
        // The theme's own control, which is supposed to add to cart.
        //
        // Tested by behaviour, not by class. This theme has four <buy-button> sites and the
        // control inside each one is classed differently: product-form__add-button on the
        // product page, the quick-buy classes on a product card, plain button--secondary in the
        // cart. What they share is that they submit the product form. A class list missed the
        // product cards and broke quick add on every collection page for a few hours on
        // 2026-09-14, which is what taught us to test the behaviour instead.
        //
        // el.type is 'submit' for a <button> with no type attribute too, which is the default
        // inside a form, so this catches the theme's markup whether or not it spells it out.
        if (el.type === 'submit') return;
        if (el.dataset[GUARD] === '1') return;
        el.dataset[GUARD] = '1';
        el.addEventListener('click', function (event) { event.stopPropagation(); });
      });
    });
  }

  guard(document);

  // Apps inject after load, and the buy buttons are re-rendered on every variant change, so a
  // one-off pass at load would only ever catch the first product view.
  //
  // Batched with setTimeout rather than requestAnimationFrame. rAF does not run in a background
  // tab, so a shopper who opened the product in a new tab and read something else first came back
  // to an unguarded wishlist button. Found while testing on 2026-09-14: the guard simply never
  // applied while the browser pane was hidden. setTimeout is throttled in a background tab but it
  // does fire, which is the property that matters here.
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; guard(document); }, 0);
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('variant:changed', function () { guard(document); });
});

// TierZero+ Subscription Logic
document.addEventListener('DOMContentLoaded', function() {
  // Remove monthly subscription labels for users with monthly subscription
  if (document.body.getAttribute('data-tz-plan') === 'monthly') {
    // Function to remove monthly subscription blocks
    function removeMonthlySubscriptionBlocks() {
      const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block_label');

      subscriptionBlocks.forEach(block => {
        const blockText = block.textContent.toLowerCase();
        if (blockText.includes('monthly') || blockText.includes('tierzero+ monthly')) {
          block.remove(); // Remove element completely from DOM
        }
      });
    }

    // Initial removal
    removeMonthlySubscriptionBlocks();

    // Watch for dynamically added content
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            const newBlocks = node.querySelectorAll('.shopify_subscriptions_app_block_label');
            newBlocks.forEach(block => {
              const blockText = block.textContent.toLowerCase();
              if (blockText.includes('monthly') || blockText.includes('tierzero+ monthly')) {
                block.remove(); // Remove element completely from DOM
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // ============================================================================
  // P3: TierZero+ Cart Membership Protection
  // Automatically removes restricted items from cart for non-members.
  // Works with Liquid-rendered data attributes from P2 (data-membership-restricted).
  // ============================================================================
  (function() {
    // Read the decision Liquid already made, do not re-derive it. This used to test the two
    // plan classes, which meant a customer holding the base membership tag with no plan tag was
    // a member to every Liquid gate and an intruder here, and their cart was emptied.
    // data-tz-member comes from snippets/membership-access.liquid via layout/theme.liquid.
    if (document.body.getAttribute('data-tz-member') === '1') return;

    /**
     * Removes all membership-restricted items from the cart via AJAX.
     * Reads data-membership-restricted and data-line-index attributes
     * set by Liquid in mini-cart and main-cart templates.
     */
    function removeRestrictedCartItems() {
      var restrictedItems = document.querySelectorAll('[data-membership-restricted="true"]');
      if (restrictedItems.length === 0) return;

      // Collect line indices (1-based from Liquid forloop.index), process highest first
      // to avoid index shifting when removing items
      var lineIndices = [];
      restrictedItems.forEach(function(item) {
        var lineIndex = parseInt(item.getAttribute('data-line-index'), 10);
        if (!isNaN(lineIndex)) {
          lineIndices.push(lineIndex);
        }
      });

      // Sort descending so we remove from the end first
      lineIndices.sort(function(a, b) { return b - a; });

      // Sequential removal to avoid race conditions
      function removeNext(index) {
        if (index >= lineIndices.length) {
          // All done — refresh the cart UI
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { scrollToTop: false }
          }));
          return;
        }

        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndices[index],
            quantity: 0
          })
        })
        .then(function() { removeNext(index + 1); })
        .catch(function(err) {
          console.error('TierZero+ Cart Protection: Error removing restricted item', err);
          removeNext(index + 1);
        });
      }

      removeNext(0);
    }

    // Run on DOM ready
    removeRestrictedCartItems();

    // Also run when cart is refreshed (e.g. after adding items via AJAX)
    document.addEventListener('cart:refresh', function() {
      // Small delay to let the Liquid re-render finish
      setTimeout(removeRestrictedCartItems, 500);
    });

    // Listen for product:added events to check if a restricted item was added
    document.addEventListener('product:added', function() {
      setTimeout(removeRestrictedCartItems, 500);
    });
  })();
});

/**
 * Search within a long facet group.
 *
 * Collection filter groups on a set page run to dozens of values — 107 checkboxes across 11
 * groups measured on /collections/chaos-origins — and finding one archetype means scrolling
 * past everything else. The Liquid renders a search input above any group at or over the
 * threshold in Theme settings; this filters that group's checkboxes as you type.
 *
 * Deliberately client-side only. It hides list items rather than re-querying, so it never
 * costs a request, never fights the facet form, and a checked value that no longer matches
 * the query stays visible — hiding an active filter would let someone submit a query they
 * cannot see.
 */
document.addEventListener('DOMContentLoaded', function () {
  var groups = document.querySelectorAll('.collection__filter-group');
  if (!groups.length) return;

  var normalise = function (value) {
    // Fold accents so "Pokemon" matches "Pokémon" — the catalogue has both.
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  };

  groups.forEach(function (group) {
    var input = group.querySelector('[data-filter-search]');
    var list = group.querySelector('.collection__filter-checkbox-list');
    if (!input || !list) return;

    var empty = group.querySelector('[data-filter-search-empty]');
    var items = Array.prototype.slice.call(list.querySelectorAll('.collection__filter-checkbox'));

    var apply = function () {
      var query = normalise(input.value);
      var visible = 0;

      items.forEach(function (item) {
        var label = item.querySelector('label');
        var checkbox = item.querySelector('input[type="checkbox"]');
        // An active filter is always shown, whatever the query. Hiding one would leave the
        // shopper submitting a narrowing they cannot see or undo.
        var isActive = checkbox && checkbox.checked;
        var matches = !query || normalise(label && label.textContent).indexOf(query) !== -1;
        var show = isActive || matches;

        item.hidden = !show;
        if (show) visible++;
      });

      if (empty) empty.hidden = visible > 0;
    };

    input.addEventListener('input', apply);
    // A search input's clear button fires `search`, not `input`, in Safari.
    input.addEventListener('search', apply);
  });
});
